import {
  createSpeechRecognition,
  type SpeechRecognitionErrorEventLike,
  type SpeechRecognitionEventLike,
  type SpeechRecognitionLike,
} from "../../../utils/speechRecognition";

export interface VoiceStateSnapshot {
  isVoiceSupported: boolean;
  isVoicePanelOpen: boolean;
  isVoiceListening: boolean;
  isVoiceApplying: boolean;
  isSubmitting: boolean;
  voiceTranscript: string;
}

export interface VoiceStatePatch {
  isVoiceSupported?: boolean;
  isVoicePanelOpen?: boolean;
  isVoiceListening?: boolean;
  voiceError?: string | null;
  voiceInfo?: string | null;
  voiceTranscript?: string;
}

interface VoiceControllerBindings {
  getState: () => VoiceStateSnapshot;
  setState: (patch: VoiceStatePatch) => void;
  appendTranscript: (transcript: string) => void;
}

export class AddProductVoiceController {
  private recognition: SpeechRecognitionLike | null = null;

  private shouldKeepVoiceListening = false;

  private voiceRestartTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly bindings: VoiceControllerBindings) {}

  onPrimaryButtonClick(): void {
    const state = this.bindings.getState();
    if (!state.isVoicePanelOpen) {
      this.bindings.setState({ isVoicePanelOpen: true });
    }

    if (state.isVoiceListening) {
      this.stopListening();
      return;
    }

    this.startListening();
  }

  closePanel(): void {
    this.stopListening();
    this.bindings.setState({
      isVoicePanelOpen: false,
      voiceInfo: null,
      voiceError: null,
    });
  }

  clearTranscript(): void {
    this.bindings.setState({
      voiceTranscript: "",
      voiceError: null,
      voiceInfo: null,
    });
  }

  setManualTranscript(value: string): void {
    this.bindings.setState({
      voiceTranscript: value,
      voiceError: null,
      voiceInfo: null,
    });
  }

  startListening(): void {
    this.bindings.setState({ isVoicePanelOpen: true });

    const state = this.bindings.getState();
    if (!state.isVoiceSupported) {
      this.bindings.setState({
        voiceError:
          "Голосовой ввод не поддерживается браузером. Используйте ручной ввод текста.",
      });
      return;
    }

    const recognition = this.ensureRecognition();
    if (!recognition) {
      this.bindings.setState({
        voiceError:
          "Не удалось инициализировать голосовой ввод. Проверьте разрешение на микрофон.",
      });
      return;
    }

    if (this.bindings.getState().isVoiceListening) {
      return;
    }

    this.shouldKeepVoiceListening = true;
    this.clearVoiceRestartTimer();
    this.bindings.setState({
      voiceError: null,
      voiceInfo: "Слушаю. Говорите свободно, текст будет дополняться.",
    });

    try {
      recognition.start();
      this.bindings.setState({ isVoiceListening: true });
    } catch (error) {
      this.bindings.setState({
        isVoiceListening: false,
        voiceError:
          error instanceof Error
            ? error.message
            : "Не удалось запустить распознавание речи",
      });
    }
  }

  stopListening(): void {
    this.shouldKeepVoiceListening = false;
    this.clearVoiceRestartTimer();

    if (!this.recognition) {
      this.bindings.setState({
        isVoiceListening: false,
        voiceInfo: "Микрофон остановлен",
      });
      return;
    }

    try {
      this.recognition.stop();
    } catch {
      this.recognition.abort();
    }

    this.bindings.setState({
      isVoiceListening: false,
      voiceInfo: "Микрофон остановлен",
    });
  }

  teardown(): void {
    this.shouldKeepVoiceListening = false;
    this.clearVoiceRestartTimer();

    if (!this.recognition) {
      return;
    }

    try {
      this.recognition.abort();
    } catch {
      // ignore
    }

    this.recognition.onresult = null;
    this.recognition.onerror = null;
    this.recognition.onend = null;
    this.recognition = null;
  }

  private ensureRecognition(): SpeechRecognitionLike | null {
    if (this.recognition) {
      return this.recognition;
    }

    const recognition = createSpeechRecognition();
    if (!recognition) {
      this.bindings.setState({ isVoiceSupported: false });
      return null;
    }

    recognition.lang = "ru-RU";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: SpeechRecognitionEventLike): void => {
      const chunks: string[] = [];
      for (
        let index = event.resultIndex;
        index < event.results.length;
        index += 1
      ) {
        const result = event.results[index];
        if (!result || !result.length) {
          continue;
        }

        const transcript = result[0].transcript.trim();
        if (!transcript || !result.isFinal) {
          continue;
        }

        chunks.push(transcript);
      }

      if (chunks.length === 0) {
        return;
      }

      this.bindings.appendTranscript(chunks.join(" "));
      this.bindings.setState({
        voiceInfo: "Речь распознана. Можно отправлять в AI.",
      });
    };

    recognition.onerror = (event: SpeechRecognitionErrorEventLike): void => {
      this.bindings.setState({ isVoiceListening: false });

      if (
        event.error === "not-allowed" ||
        event.error === "service-not-allowed"
      ) {
        this.shouldKeepVoiceListening = false;
        this.clearVoiceRestartTimer();
        this.bindings.setState({
          voiceError: "Нет доступа к микрофону. Разрешите микрофон в браузере.",
        });
        return;
      }

      if (event.error === "no-speech") {
        this.bindings.setState({
          voiceInfo: "Пауза без речи. Продолжаю слушать...",
        });
        return;
      }

      if (event.error === "aborted") {
        this.bindings.setState({
          voiceInfo: this.shouldKeepVoiceListening
            ? "Перезапускаю микрофон..."
            : "Микрофон остановлен",
        });
        return;
      }

      this.bindings.setState({
        voiceError: `Ошибка голосового ввода: ${event.error}`,
      });
    };

    recognition.onend = (): void => {
      this.bindings.setState({ isVoiceListening: false });

      if (this.shouldKeepVoiceListening) {
        this.scheduleVoiceRestart();
      }
    };

    this.recognition = recognition;
    return recognition;
  }

  private clearVoiceRestartTimer(): void {
    if (this.voiceRestartTimer === null) {
      return;
    }

    clearTimeout(this.voiceRestartTimer);
    this.voiceRestartTimer = null;
  }

  private scheduleVoiceRestart(): void {
    this.clearVoiceRestartTimer();

    this.voiceRestartTimer = setTimeout(() => {
      this.voiceRestartTimer = null;

      if (!this.shouldKeepVoiceListening) {
        return;
      }

      const recognition = this.ensureRecognition();
      if (!recognition) {
        return;
      }

      try {
        recognition.start();
        this.bindings.setState({
          isVoiceListening: true,
          voiceInfo: "Слушаю. Говорите свободно, текст будет дополняться.",
        });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message.toLocaleLowerCase("ru-RU")
            : "";

        if (
          message.includes("notallowed") ||
          message.includes("denied") ||
          message.includes("permission")
        ) {
          this.shouldKeepVoiceListening = false;
          this.bindings.setState({
            voiceError:
              "Нет доступа к микрофону. Разрешите микрофон в браузере.",
          });
          return;
        }

        this.scheduleVoiceRestart();
      }
    }, 180);
  }
}
